
import { Component, Prop, Vue } from "vue-property-decorator";
import { hasMoreThanTwoDecimals, multiply } from "@/utils/number";
import { DetailQueryQtyStepProductGroupInfo } from "./PackingListProductListDialog.vue";

/** PI产品列表项 */
export interface PiProductItem {
  piCode?: string;
  unitPrice?: number;
  saleQuantityStep?: number;
  currency?: string;
  productGroup?: DetailQueryQtyStepProductGroupInfo;
}

@Component({
  name: "PiProductListDialog",
})
export default class PiProductListDialog extends Vue {
  @Prop({ default: false }) private visible!: boolean;
  @Prop({ default: () => [] }) private items!: PiProductItem[];
  @Prop({ default: false }) private loading!: boolean;

  get warningRowCount(): number {
    return (this.items || []).filter((row) => this.hasRowWarning(row)).length;
  }

  private getQty(row: PiProductItem): number | undefined {
    return row.productGroup && row.productGroup.qty;
  }

  private displayValue(value: number | string | null | undefined): string | number {
    if (value === 0) {
      return 0;
    }
    return value == null || value === "" ? "" : value;
  }

  private isMultipleOf(value: number | string, step: number | string): boolean {
    const v = Number(value);
    const s = Number(step);
    if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) {
      return true;
    }
    const ratio = v / s;
    return Math.abs(ratio - Math.round(ratio)) < 1e-8;
  }

  private shouldShowQuantityStepHint(row: PiProductItem): boolean {
    const step = Number(row.saleQuantityStep);
    return Number.isFinite(step) && step > 1;
  }

  private hasQtyWarning(row: PiProductItem): boolean {
    const qty = this.getQty(row);
    const { saleQuantityStep } = row;
    if (qty == null || saleQuantityStep == null) {
      return false;
    }
    return !this.isMultipleOf(qty, saleQuantityStep);
  }

  private getTotalPrice(row: PiProductItem): number | "" {
    const qty = this.getQty(row);
    if (qty == null || row.unitPrice == null) {
      return "";
    }
    const qtyNum = Number(qty);
    const unitPrice = Number(row.unitPrice);
    if (!Number.isFinite(qtyNum) || !Number.isFinite(unitPrice)) {
      return "";
    }
    return multiply(qtyNum, unitPrice, 10);
  }

  private currencySymbol(currency?: string): string {
    const code = (currency || "").toUpperCase();
    if (code === "USD") {
      return "$";
    }
    if (code === "EURO") {
      return "€";
    }
    if (code === "RMB") {
      return "￥";
    }
    return currency || "";
  }

  private formatPriceWithCurrency(
    row: PiProductItem,
    value: number | string | null | undefined
  ): string {
    const display = this.displayValue(value);
    if (display === "") {
      return "";
    }
    const symbol = this.currencySymbol(row.currency);
    return symbol ? `${symbol} ${display}` : String(display);
  }

  private formatUnitPrice(row: PiProductItem): string {
    return this.formatPriceWithCurrency(row, row.unitPrice);
  }

  private formatTotalPrice(row: PiProductItem): string {
    const totalPrice = this.getTotalPrice(row);
    return this.formatPriceWithCurrency(row, totalPrice === "" ? "" : totalPrice);
  }

  private hasTotalPriceWarning(row: PiProductItem): boolean {
    const totalPrice = this.getTotalPrice(row);
    if (totalPrice === "") {
      return false;
    }
    return hasMoreThanTwoDecimals(totalPrice);
  }

  private hasRowWarning(row: PiProductItem): boolean {
    return this.hasQtyWarning(row) || this.hasTotalPriceWarning(row);
  }

  private qtyWarningTip(row: PiProductItem): string {
    return `【数量】(${this.getQty(row)})不是【销售计价单位】(${row.saleQuantityStep})的倍数，将不能生成计划。`;
  }

  private quantityStepHintTip(row: PiProductItem): string {
    return `销售计价单位:${row.saleQuantityStep}\n\n注意: 【数量】必须是【销售计价单位】的倍数。因要满足PI内单个产品的总价必须要两位小数以内，以便收到正确货款。`;
  }

  private totalPriceWarningTip(row: PiProductItem): string {
    return `【总价】(${this.getTotalPrice(row)})不是两位小数，将不能生成计划。`;
  }

  private handleClose() {
    this.$emit("update:visible", false);
  }
}
